/**
 * Field Matching Service
 *
 * This file handles the logic for checking and matching existing fields
 * when importing a solution.
 */

import {
  createEmptyFieldUpdates,
  getReportingTemplateByKey,
  getReportingTemplateFieldMappingUpdates,
} from "../utils/solutionImportHelpers";

const parseJsonObject = (value: string): any | null => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
};

const getItemByExternalReference = (
  items: any[] = [],
  fieldKey: string,
  fieldValue: any
): any | null => {
  const parsedKey = parseJsonObject(fieldKey);
  if (parsedKey) return parsedKey;

  return (
    items.find(
      (item: any) => item?.uuid === fieldKey || item?.uuid === fieldValue
    ) || null
  );
};

const getMatchName = (value: any): string =>
  String(value?.label || value?.Label || value?.name || value?.Name || "");

const findSameNameMatch = (items: any[] = [], source: any): any => {
  const sourceName = getMatchName(source);
  if (!sourceName) return undefined;

  return items.find((item: any) => getMatchName(item) === sourceName);
};

/**
 * Check and match existing contact fields
 */
export const checkMatchingContactFields = async (
  importedJSON: any,
  findMatchingContactFields: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const contactFieldPromises = Object.entries(
    importedJSON.Bag.External.ContactField || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const contactField = getItemByExternalReference(
      importedJSON.Item?.ContactFields || [],
      fieldKey,
      fieldValue
    );
    if (!contactField) {
      updates.ContactField[fieldKey] = "";
      return;
    }
    const contactFieldsData = await findMatchingContactFields(contactField);
    if (contactFieldsData?.data?.fields?.length > 0) {
      const matchingField =
        contactFieldsData.data.fields.find(
          (c: any) => c.label === contactField.label
        ) || contactFieldsData.data.fields[0];
      updates.ContactField[fieldKey] = matchingField.uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
      if (matchingField.options && matchingField.options.length > 0) {
        matchingField.options.forEach((field: any) => {
          const fieldData = {
            ...contactField.options?.find(
              (f: any) => f.label === field.label
            ),
          };
          delete fieldData.uuid;
          delete fieldData.order;
          if (fieldData) {
            const optionKey = Object.entries(
              importedJSON.Bag.External.ContactFieldOption || {}
            ).find(
              ([externalOptionKey, externalOptionValue]: [string, any]) =>
                externalOptionKey === fieldData.uuid ||
                externalOptionValue === fieldData.uuid
            )?.[0];
            updates.ContactFieldOption[
              optionKey || JSON.stringify({ field: contactField, option: fieldData })
            ] = field.uuid;
          }
        });
      }
    } else {
      updates.ContactField[fieldKey] = "";
    }
  });

  await Promise.all(contactFieldPromises);
};

/**
 * Check and match existing contact sets
 */
export const checkMatchingContactSets = async (
  importedJSON: any,
  contactSetsData: any,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const contactSetPromises = Object.entries(
    importedJSON.Bag.External.ContactSet || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    if (contactSetsData?.contactSets?.length > 0) {
      const contactSet = getItemByExternalReference(
        importedJSON.Item?.ContactGroups || [],
        fieldKey,
        fieldValue
      );
      if (!contactSet) {
        updates.ContactSet[fieldKey] = "";
        return;
      }
      const matchingContactSet =
        findSameNameMatch(contactSetsData.contactSets, contactSet) ||
        contactSetsData.contactSets[0];
      updates.ContactSet[fieldKey] = matchingContactSet.uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
    } else {
      updates.ContactSet[fieldKey] = "";
    }
  });

  await Promise.all(contactSetPromises);
};

/**
 * Check and match existing reporting templates
 */
export const checkMatchingReportingTemplates = async (
  importedJSON: any,
  findMatchingTemplates: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const reportingTemplatePromises = Object.entries(
    importedJSON.Bag.External.ReportingTemplate || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const sourceTemplate =
      getReportingTemplateByKey(importedJSON, fieldKey) ||
      getReportingTemplateByKey(importedJSON, fieldValue);
    if (!sourceTemplate) {
      updates.ReportingTemplate[fieldKey] = "";
      return;
    }

    const matchingTemplatesData = await findMatchingTemplates(sourceTemplate);
    if (matchingTemplatesData?.data?.data?.length > 0) {
      const sourceFields = sourceTemplate.fields || [];
      const compatibleTemplates = matchingTemplatesData.data.data.filter(
        (template: any) =>
          sourceFields.every((sourceField: any) =>
            template.fields?.some((field: any) => field.label === sourceField.label)
          )
      );
      const matchingTemplate =
        findSameNameMatch(compatibleTemplates, sourceTemplate) ||
        compatibleTemplates[0];

      if (!matchingTemplate) {
        updates.ReportingTemplate[fieldKey] = "";
        return;
      }

      updates.ReportingTemplate[fieldKey] = matchingTemplate.uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
      Object.entries(
        getReportingTemplateFieldMappingUpdates(
          sourceTemplate,
          matchingTemplate,
          importedJSON.Bag.External.ReportingTemplateField || {}
        )
      ).forEach(([templateFieldKey, templateFieldValue]) => {
        updates.ReportingTemplateField[templateFieldKey] = templateFieldValue;
      });
    } else {
      updates.ReportingTemplate[fieldKey] = "";
    }
  });

  await Promise.all(reportingTemplatePromises);
};

/**
 * Check and match existing reporting topics
 */
export const checkMatchingTopics = async (
  importedJSON: any,
  reportingTopicsData: any,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const topicPromises = Object.entries(
    importedJSON.Bag.External.Topic || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const matchingTopic = reportingTopicsData?.data.find((topic: any) => {
      const topicData = getItemByExternalReference(
        importedJSON.Item?.ReportingTopics || [],
        fieldKey,
        fieldValue
      );
      if (!topicData) return false;
      return topic.label === topicData.label;
    });
    if (matchingTopic) {
      updates.Topic[fieldKey] = matchingTopic.uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
    } else {
      updates.Topic[fieldKey] = "";
    }
  });

  await Promise.all(topicPromises);
};

export const checkMatchingDataStructureFields = async (
  importedJSON: any,
  solutionData: any,
  findMatchingApiDataCollections: (data: { paths: string[] }) => Promise<any>,
  updates: Record<string, Record<string, string>>,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<void> => {
  const getSourceDataStructureKeys = (dataStructureKey: string): string[] => {
    const keys = new Set<string>([dataStructureKey]);

    (solutionData?.Components || []).forEach((component: any) => {
      const sourceKey = component?.Bag?.External?.DataStructure?.[dataStructureKey];
      if (sourceKey) keys.add(String(sourceKey));
    });

    return Array.from(keys);
  };

  const getRequiredPaths = (dataStructureKey: string): string[] => {
    const paths = new Set<string>();
    const sourceDataStructureKeys = new Set(
      getSourceDataStructureKeys(dataStructureKey)
    );

    (solutionData?.Components || []).forEach((component: any) => {
      const sourceVariableUuids = (component?.Item?.InputArguments || [])
        .filter(
          (argument: any) =>
            argument.Format === "json" &&
            sourceDataStructureKeys.has(argument.DataStructure)
        )
        .map((argument: any) => argument.UUID)
        .filter(Boolean);

      if (!sourceVariableUuids.length) return;

      const sourceVariables = new Set(sourceVariableUuids);
      (component?.Item?.EventHandlers || []).forEach((eventHandler: any) => {
        (eventHandler.Rules || []).forEach((rule: any) => {
          (rule.Actions || []).forEach((action: any) => {
            if (action.Type !== "extract:from-data-structure") return;

            const options = action.Options || [];
            const sourceOption = options.find(
              (option: any) => option.Type === "json"
            );
            if (!sourceVariables.has(sourceOption?.Value)) return;

            options
              .filter((option: any) => option.Type === "json:path")
              .forEach((option: any) => {
                const path =
                  importedJSON.Bag?.External?.DataStructureField?.[
                    option.Value
                  ] || option.Value;
                if (path) paths.add(String(path));
              });
          });
        });
      });
    });

    return Array.from(paths);
  };

  const dataStructurePromises = Object.entries(
    importedJSON.Bag.External.DataStructure || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const requiredPaths = getRequiredPaths(fieldKey);
    if (!requiredPaths.length) {
      updates.DataStructure[fieldKey] = fieldValue || "";
      return;
    }

    const matchingDataCollectionsData = await findMatchingApiDataCollections({
      paths: requiredPaths,
    });
    const matchingDataCollection =
      matchingDataCollectionsData?.data?.collections?.[0];

    if (matchingDataCollection?.Uuid) {
      updates.DataStructure[fieldKey] = matchingDataCollection.Uuid;
      setAutomaticallySelectedFields((prev) => [...prev, fieldKey]);
    } else {
      updates.DataStructure[fieldKey] = fieldValue || "";
    }
  });

  await Promise.all(dataStructurePromises);

  Object.entries(importedJSON.Bag.External.DataStructureField || {}).forEach(
    ([fieldKey, fieldValue]: [string, any]) => {
      updates.DataStructureField[fieldKey] = fieldValue || fieldKey;
    }
  );
};

/**
 * Orchestrates all field matching operations
 */
export const handleCheckMatchingFields = async (
  importedJSON: any,
  queries: {
    findMatchingContactFields: (data: any) => Promise<any>;
    findMatchingTemplates: (data: any) => Promise<any>;
    contactSetsData: any;
    reportingTopicsData: any;
    solutionData: any;
    findMatchingApiDataCollections: (data: { paths: string[] }) => Promise<any>;
  },
  setDeployProgress: (progress: string) => void,
  setAutomaticallySelectedFields: React.Dispatch<React.SetStateAction<string[]>>
): Promise<Record<string, Record<string, string>>> => {
  setDeployProgress("Checking existing fields...");

  const updates: Record<string, Record<string, string>> = importedJSON.Bag
    .External || createEmptyFieldUpdates();

  setDeployProgress("Checking required fields...");

  // Check all field types in parallel
  await Promise.all([
    checkMatchingContactFields(
      importedJSON,
      queries.findMatchingContactFields,
      updates,
      setAutomaticallySelectedFields
    ),
    checkMatchingContactSets(
      importedJSON,
      queries.contactSetsData,
      updates,
      setAutomaticallySelectedFields
    ),
    checkMatchingReportingTemplates(
      importedJSON,
      queries.findMatchingTemplates,
      updates,
      setAutomaticallySelectedFields
    ),
    checkMatchingTopics(
      importedJSON,
      queries.reportingTopicsData,
      updates,
      setAutomaticallySelectedFields
    ),
    checkMatchingDataStructureFields(
      importedJSON,
      queries.solutionData,
      queries.findMatchingApiDataCollections,
      updates,
      setAutomaticallySelectedFields
    ),
  ]);

  setTimeout(() => {
    setDeployProgress("");
  }, 700);

  return updates;
};
